// GameScene — the M0 playable slice. Owns a Sim, renders its snapshot every frame, drives the
// human via keyboard/touch, lets the AI play itself, and consumes the sim's EventLog for juice.
//
// Render contract (frozen Core is the source of truth — this scene only reads snapshots/events):
//   - rule banner, a cols x rows grid of neutral tiles (correctness is NOT pre-revealed by color),
//   - human muncher (cyan circle w/ mouth), AI muncher (magenta triangle), hazards (#FF4D6D),
//   - HUD: score, combo meter, lives, a draining timer bar, quota progress.
import Phaser from 'phaser';
import type { Snapshot, Direction, BoardContent } from '../../core';
import { Sim, difficultyForBoard, dailyDifficulty, DAILY_MUTATORS, DEFAULT_GRID, AI_TIERS } from '../../core';
import { getSeedBoard, SEED_BOARDS } from '../../data/seedPack';
import { RivalPersona, resetGrudgeColdOpen } from '../rivalPersona';
import { mutatorForDay } from '../dailyMutator';
import { PALETTE, hex, FONT } from '../keys';
import { readBoot } from '../registryKeys';
import { decodeGhost, type GhostOutcome } from '../ghostRoute';
import { buildGhostOverlay, pruneGhostMarkers, destroyGhostMarkers, type GhostMarkerMap } from '../ghostOverlay';
import { buildGhostTrack, type GhostTrack } from '../ghostTrack';
import { GhostRacer } from '../ghostRacer';
import type { LadderRace } from '../ghostLadder';
import type { SeriesState } from '../series';
import {
  floatText,
  clearActiveFloats,
} from '../juice';
import { audio } from '../audio';
import { addBackdrop } from '../backdrop';
import { haptic, motionAllowed } from '../settings';
import { isLandscape } from '../stage';
import { gridForViewport } from '../viewport';
import { setActiveGameScene } from '../../hooks';
import { resetCaptionSlot } from '../captionSlot';
import { handleFatal } from '../errorBoundary';
import { track, trackOnce, type BoardSource } from '../../analytics/client';
import { replaceRun } from '../run';
import {
  isNoviceScanUnlocked,
  noviceBoardFor,
  noviceBoardSeed,
  noviceDifficultyForBoard,
} from '../noviceRun';
import {
  PLAYER,
  AI,
  STEP_FEED_MS,
  type CellView,
  type InputCmd,
} from '../game/types';
import * as Analytics from '../game/analyticsBridge';
import * as Round from '../game/roundFlow';
import * as Pause from '../game/pauseModal';
import * as Events from '../game/eventFeedback';
import * as Input from '../game/inputController';
import * as Hud from '../game/hudController';
import * as Units from '../game/unitsView';
import * as Board from '../game/boardView';
import * as Ghost from '../game/ghostController';
import * as Director from '../game/musicDirector';
import { Broadcast, resetBoutIntro, BROADCAST } from '../game/broadcast';

// #41 KEYBOARD DAS/ARR: the delayed-auto-shift — hold time before a held movement key begins to
// auto-repeat. The advertised feel is DAS 140 → ARR 90; ARR reuses STEP_FEED_MS (90), the same
// held-direction cadence the drag-glide funnel already paces at. Render-side only (shapes the
// command stream through dispatch(); never touches Core or sim semantics).
const KEY_DAS_MS = 140;

interface GameInit {
  topic?: string;
  seed?: number;
  ai?: boolean;
  /** Menu-selected Rival mode starts with a solo warmup board, then joins the rival on NEXT. */
  rivalWarmup?: boolean;
  boardIndex?: number;
  /** Dev/capture override: restore the deception ("flip") hazards on this board even though they are
   *  off by default (ENABLE_DECEPTION_HAZARDS). Used by the liar-active regression screenshot. */
  forceDeception?: boolean;
  /** P0 analytics: generated, seed, or seed fallback. Renderer-only; Core does not inspect it. */
  boardSource?: BoardSource;
  /**
   * M1: a ready-to-play board (generated OR seed) handed in from the Menu. When present it is used
   * verbatim and the topic-based seed lookup is skipped. Absent (M0 / autoPlay / NEXT) -> the scene
   * falls back to getSeedBoard(topic) exactly as before, so the M0 gate is unchanged.
   */
  content?: BoardContent;
  /** M3: the human-facing topic label for stats/leaderboard/share copy (defaults to content's). */
  topicLabel?: string;
  /** M3: true when this run was launched from the Daily button / a daily deep link. */
  isDaily?: boolean;
  /** First-run teach-by-playing boards. Renderer-only; Core still receives plain content + params. */
  noviceRun?: boolean;
  /** P1.2 challenge: a friend's score to beat on this seed+topic (0/undefined = not a challenge). */
  challengeTarget?: number;
  /** P1.2b: a friend's compact route to race as a ghost ('' = none). */
  challengeGhost?: string;
  /** GHOST DUELS Slice A: 'last-daily' races the LOCAL saved daily ghost (offline; bv/seed/m/topic-pinned). */
  ghostSource?: 'last-daily' | '';
  /** GHOST DUELS Slice C: a specific ladder challenger to race (record + rung). Pin-checked in init; a miss
   *  fails open to a plain daily. Takes precedence over the self-race ghostSource. */
  ladderGhost?: LadderRace | null;
  /** P1.2c best-of-three series state, carried like challengeTarget (null = not a series). */
  series?: SeriesState | null;
}


export class GameScene extends Phaser.Scene {
  sim!: Sim;
  seed = 11;
  boardIndex = 0;
  topic = '';
  /** M3: human-facing topic label (for results/share/leaderboard); falls back to the slug. */
  topicLabel = '';
  /** M3: did this run come from the Daily button / a daily deep link? Drives the daily streak. */
  isDaily = false;
  /** Daily twist id (-1 = not a daily). Resolved once in init from the deep link or today's date. */
  dailyMutatorId = -1;
  /** M3: peak combo reached this board (for stats + share card + match summary). */
  peakCombo = 0;
  /** M1: board passed in from the Menu (generated/seed). null -> fall back to seed lookup by topic. */
  pendingContent: BoardContent | null = null;
  aiEnabled = true;
  rivalWarmup = false;
  showFps = false;
  /** TEACH beat: fire the "locked" callout only on the player's first ever arm, not the AI's. */
  taughtArm = false;
  /** TEACH beat: after the first wrong munch, nudge SCAN once if the player still has charges. */
  taughtWrongScan = false;
  /** TEACH beat: first-board, non-blocking SCAN affordance before the player has made a mistake. */
  taughtEarlyScan = false;
  /** #34: one-shot "FIRST BITE!" hero float on the human's first correct CHOMP of novice board 0. */
  firstBiteShown = false;
  /** #37: one-shot threading coach beat on the first armed traversal of the novice fenced board (index 1). */
  taughtNoviceThread = false;
  /** ?debug=1 (the eyes/capture harness) — suppress the board-reveal flourish so screenshots see the
   *  final full-scale board deterministically (audio is already a no-op headless without a gesture). */
  debug = false;
  /** ?intro=1 — force the pre-round topic flash to show even under ?debug=1, so social-replay clip
   *  capture (which runs with debug=1) can include the topic card WITHOUT changing the deterministic
   *  create()-time screenshot path (debug=1 alone still suppresses it). Purely additive opt-in. */
  introForced = false;
  /** Dev/capture override (GameInit.forceDeception): re-enable the off-by-default Liar/Scrambler on
   *  this board so the liar-active regression screenshot has a real Liar to shoot. */
  forceDeception = false;
  /** P0 analytics: generated, seed, or seed fallback source for this board. */
  boardSource: BoardSource = 'unknown';
  noviceRun = false;
  /** P1.2 challenge: a friend's score to beat on this exact board (0 = not a challenge). */
  challengeTarget = 0;
  ghostOutcomes: GhostOutcome[] = [];
  /** P1.2b: the faint challenge-ghost footprint markers (retired one-way as the player reaches cells). */
  ghostMarkers: GhostMarkerMap = new Map();
  /** GHOST DUELS Slice A: how to source a live ghost racer ('last-daily' = the local saved daily). */
  ghostSource: 'last-daily' | '' = '';
  /** GHOST DUELS Slice B: a friend's KV-stored ghost id from a &gr deep link ('' = none). Fetched async in
   *  create(); a live spectral replay when the blob's pin matches, else falls through to static footprints. */
  ghostRemoteId = '';
  /** GHOST DUELS Slice C: the ladder challenger this run is duelling (record + rung), or null. Set in init;
   *  cleared to null there if its pin/decode fails so no bogus rung verdict is handed to ResultScene. */
  ladderRace: LadderRace | null = null;
  /** Decoded ghost track resolved in init() (pin-matched), consumed to build the racer in create(). */
  pendingGhostTrack: GhostTrack | null = null;
  /** The spectral self-race replay entity (renderer-only; never touches the Sim). */
  ghostRacer?: GhostRacer;
  /** Display name for the ghost racer (from the saved envelope). */
  ghostName = 'YOU';
  /** Once-guard so the daily ghost is minted to localStorage at most once per board. */
  ghostRecorded = false;
  /** Right-anchored screen anchor for the ghost score chip (computed in buildHud). */
  ghostChipAnchor = { x: 0, y: 0 };
  series: SeriesState | null = null;
  scanUnlocked = true;
  timerVisible = true;
  /** Sim time at the start of this board, used for privacy-safe elapsedMs dimensions. */
  boardStartSimMs = 0;
  /** Guard so completed/abandoned analytics are emitted at most once. */
  boardTerminalTracked = false;

  // layout
  gridX = 0;
  gridY = 0;
  cellW = 0;
  cellH = 0;
  cols: number = DEFAULT_GRID.cols;
  rows: number = DEFAULT_GRID.rows;
  boardW = 0;
  /** NEUTRAL tile border, blended a step toward the board theme accent (decoration only — state
   *  borders correct/hazard/armed/scan are untouched). Computed once per board in buildGrid. */
  themeCellStroke: number = PALETTE.lineStrong;

  // display objects
  cellViews: CellView[] = [];
  /** cell ids whose label is currently dimmed because a muncher/hazard sprite sits on them. */
  occludedLabels = new Set<number>();
  /** S3 item 8: cached board-rect geometry mask + its source Graphics, used to clip the SCAN ripple
   *  (and any board-local VFX) so an edge-column effect never washes off-screen. Built lazily. */
  boardClipGfx?: Phaser.GameObjects.Graphics;
  boardClipMask?: Phaser.Display.Masks.GeometryMask;
  humanSprite!: Phaser.GameObjects.Container;
  aiSprite!: Phaser.GameObjects.Container;
  /** Owner-feedback slice: persistent auras parented to the human muncher, toggled from the snapshot
   *  each frame in updatePlayerAuras. SAFE GROUND = a translucent green shield DOME (bubble fill + bright
   *  rim). CHOMP SURGE = an electric blue-white ring (white-hot edge over a deeper-blue core) + a reused
   *  spark emitter. All created invisible in makeHumanSprite; no per-frame allocation. */
  safeGroundRing?: Phaser.GameObjects.Arc;
  safeGroundBubble?: Phaser.GameObjects.Arc;
  surgeRing?: Phaser.GameObjects.Arc;
  surgeCore?: Phaser.GameObjects.Arc;
  /** Persistent, reused CHOMP-SURGE spark emitter (created lazily; `null` = texture unavailable/headless). */
  surgeSparks?: Phaser.GameObjects.Particles.ParticleEmitter | null;
  /** One-shot guard so the SAFE-GROUND coach line teaches at most once per session (registry-backed). */
  safeGroundCoachShown = false;
  /** A1 comet trails — ONE persistent pooled soft-dot emitter per muncher, created lazily on the
   *  first move and reused for every subsequent move. `null` = dot texture unavailable (headless),
   *  cached so we never retry per move; `undefined` = not created yet. Destroyed on SHUTDOWN. */
  playerTrailEmitter?: Phaser.GameObjects.Particles.ParticleEmitter | null;
  aiTrailEmitter?: Phaser.GameObjects.Particles.ParticleEmitter | null;
  aiPressureCue?: Phaser.GameObjects.Graphics;
  hazardSprites = new Map<number, Phaser.GameObjects.Container>();
  /**
   * Per-hazard steady-state "I am dangerous" red aura pulse tweens, keyed by hazard id, so the threat
   * pulse can be killed cleanly when a hazard despawns or the scene shuts down (no leaked tweens). The
   * aura *objects* live inside the hazard container and die with it; only the tween needs explicit kill.
   */
  hazardAuraTweens = new Map<number, Phaser.Tweens.Tween>();

  // HUD
  scoreText!: Phaser.GameObjects.Text;
  comboText!: Phaser.GameObjects.Text;
  comboIcon?: Phaser.GameObjects.Graphics;
  /** Static "COMBO" eyebrow under the centred multiplier; toggled with the live combo. */
  comboSubLabel?: Phaser.GameObjects.Text;
  /** Tactical-OLED redesign: hazard hearts (kit heartRow) replace the legacy "♥ N" text. */
  heartRowC?: Phaser.GameObjects.Container;
  heartRowSet?: (lives: number) => void;
  /** Quota progress chip: N small pips beside SCORE that FILL as correct cells are eaten. */
  quotaPips: Phaser.GameObjects.Rectangle[] = [];
  /** Last eatenCorrect count we drew so we only re-tint a pip (and pop it) on a real change. */
  prevEatenCorrect = -1;
  /** Last human score we drew, so the SCORE HUD scale-pops only on a real increase (-1 = first draw). */
  prevScore = -1;
  /** Synthesized "distance from win" verdict (center of the rail's lower sub-line): fuses quota-to-go
   *  with the AI margin into one plain-language token — "2 TO WIN" / "MATCH POINT" / "BEHIND 60" — so
   *  the player reads how close winning is in ONE place instead of joining pips + the rival gap. */
  winStatusText!: Phaser.GameObjects.Text;
  /** M1: the gold chip drawn behind winStatusText ("N TO WIN"/"MATCH POINT" reward pill; hazard when behind). */
  winStatusPill?: Phaser.GameObjects.Graphics;
  /** Last quota-to-go drawn, so the near-quota "LAST ONE" buildup callout fires once on the →1 step. */
  prevToGo = -1;
  scanText!: Phaser.GameObjects.Text;
  /**
   * WARN-1 RIVAL READOUT: a compact, always-visible in-game dual chip so the competition is FELT
   * during play (the rival's score previously only appeared on the result screen, making the AI read
   * like a bot). YOU is cyan▲ / RIVAL is magenta▼ (color + shape + label — accessible double-coding).
   * Only built when the rival is enabled; all parts are torn down with the rest of the HUD on init().
   */
  rivalReadout?: Phaser.GameObjects.Container;
  /** BYT3's face + voice (emote avatar on the readout, reactive barks). Renderer-only persona. */
  rivalPersona?: RivalPersona;
  /** STRETCH Lane A — the broadcast HUD package (RIVAL PRESSURE bar + #44 menace beat + bout intro).
   *  Renderer-only, pure Snapshot consumer; only built for a non-ladder BYT3 match. Rebuilt per board. */
  broadcast?: Broadcast;
  /** Board-start "thinking" tell: "…" on the rival readout until its first act (grace window). */
  rivalThinkingText?: Phaser.GameObjects.Text;
  rivalGraceMs = 0;
  rivalHasActed = false;
  rivalReadoutBg?: Phaser.GameObjects.Graphics;
  rivalScoreText?: Phaser.GameObjects.Text;
  /** Mirror the rival's score + the lead value/sign so the chip only re-lays-out + flashes on a real
   *  change (dirty-check) and the pulse only fires on a rival score or lead flip. */
  prevRivalScore = -1;
  prevLead = 0;
  prevLeadSign = 0;
  underTextBg?: Phaser.GameObjects.Graphics;
  underText!: Phaser.GameObjects.Text;
  /** Fix 3 (zero-overlap): true while the 'YOU ARE ON' status line is faded out for a crescendo banner
   *  sharing the above-grid band, so the fade is edge-triggered (never re-tweened every frame). */
  underTextDimmed = false;
  /** S9 FIX-C: while time.now is before this, the 'YOU ARE ON' status line yields (alpha 0) exactly like
   *  it does for a hero stamp — armed by the showTeachBeat wrapper for the portrait teach card's life so
   *  the card's plate can never clip the status text (G0 AMATEUR 4). */
  underTextYieldUntilMs = 0;
  commandHintText!: Phaser.GameObjects.Text;
  commandHintBg?: Phaser.GameObjects.Graphics;
  /** S3 item 2: the ONE anchored toast slot Y (above the dock). The persistent command hint, the
   *  SKIPPED×N pill, and the SCAN!/CHOMP! verb floats all key off this single anchor and are mutually
   *  exclusive there — one visible at a time — so they can never stack (the audit's #1 dock collision,
   *  previously caused by two mismatched dockTop constants). Computed once per board in buildHud. */
  toastSlotY = 0;
  /** S9 FIX-C caption-band arbiter: the toast slot's X — W/2 in portrait, the right command-rail centre
   *  in landscape (captions dock there, structurally off the near-full-height board). */
  toastSlotX = 0;
  /** S9 FIX-C: the live SCAN teach bubble (lowest caption lane), so a coach line / status pill can
   *  retire it instantly instead of stacking on it. */
  scanTeachBubble?: Phaser.GameObjects.Container;
  /** One-shot latch for the bubble's deferred retry when it fires while a coach caption is live. */
  scanBubbleRetried = false;
  safePassBanner?: Phaser.GameObjects.Container;
  safePassBannerText?: Phaser.GameObjects.Text;
  safePassBannerBg?: Phaser.GameObjects.Graphics;
  safePassCount = 0;
  safePassResetTimer?: Phaser.Time.TimerEvent;
  // Edge flag for the in-field PRIMED cue: fire the ephemeral near-muncher chip once each time the
  // human newly reaches the Gauntlet threshold (re-armed after a munch drops armedTraversals below it).
  primedFieldCueShown = false;
  scanButton!: Phaser.GameObjects.Container;
  munchButton!: Phaser.GameObjects.Container;
  /** Tactical-OLED redesign: kit timerPill (clock + gradient pill + m:ss) at the bottom of the play area. */
  timerPillC?: Phaser.GameObjects.Container;
  timerPillSet?: (frac: number, secs: number) => void;
  selectionRing!: Phaser.GameObjects.Graphics;
  timerMaxMs = 1;
  /** Disabled-CHOMP affordance: transient cyan outlines around legal neighbor moves. */
  legalMoveHints: Phaser.GameObjects.Graphics[] = [];
  legalMoveHintKey = '';
  /** HUD-TENSION state: held red EDGE vignette (near-miss), shown when lives<=1 or the timer is low. */
  tensionVeil?: Phaser.GameObjects.Graphics;
  tensionOn = false;
  /** HUD-TENSION warm backdrop wash (mockup: the radial shifts toward #1A1320), faded by setTensionState. */
  tensionWarmth?: Phaser.GameObjects.Rectangle;
  /** kit heartRow per-heart Graphics handles (+ draw size) so tension can pulse the LAST remaining heart. */
  heartGs: Phaser.GameObjects.Graphics[] = [];
  heartSize = 15;
  heartTensionOn = false;
  tensionHeartTween?: Phaser.Tweens.Tween;
  /** Timer LOW state (fill solid-hazard <15%): soft glow behind the pill + a gentle breathing pulse. */
  timerGlow?: Phaser.GameObjects.Graphics;
  timerGlowTween?: Phaser.Tweens.Tween;
  timerPillPulse?: Phaser.Tweens.Tween;
  timerLowOn = false;
  /** Final-5s countdown throb tween — tracked so it never kills the low-state alpha pulse above. */
  timerThrobTween?: Phaser.Tweens.Tween;
  /** Resting x of the rule text, so the board-entrance flourish slides it in without breaking left-align. */
  bannerRestX = 0;

  // --- visual-depth pass (renderer-only) -----------------------------------
  /** Rule-banner objects, captured so the board-entrance can slide them in. */
  bannerBg?: Phaser.GameObjects.Rectangle;
  bannerRule?: Phaser.GameObjects.Text;
  /** Board-stage depth layer (faint inner glow/vignette) drawn BELOW the panel; drifts under motion. */
  boardDepth?: Phaser.GameObjects.Graphics;
  boardDepthBaseX = 0;
  boardDepthBaseY = 0;
  boardDepthElapsed = 0;
  /** IDLE LIFE: per-cell sheen rects whose alpha is breathed by ONE update handler (phase-offset wave). */
  idleSheens: { sheen: Phaser.GameObjects.Rectangle; phase: number }[] = [];
  /** Accumulated ms for the idle-sheen breath wave (advanced in the per-cell update handler). */
  idleSheenElapsed = 0;
  /**
   * WARN-4 muncher IDLE LIFE: the bobbed BODY child of each muncher (art image or procedural body) plus
   * its container, its rest-y, and a phase offset. ONE update handler gently bobs/breathes them while
   * they're not mid-step so the munchers feel alive (purely cosmetic — never moves the grid position).
   * Cleared in init(); the handler is unbound on SHUTDOWN. Skipped entirely under reduced-motion.
   */
  idleMunchers: {
    container: Phaser.GameObjects.Container;
    body: Phaser.GameObjects.GameObject & { y: number; setY: (v: number) => unknown };
    restY: number;
    phase: number;
  }[] = [];
  /** Accumulated ms for the muncher idle bob wave (advanced in the idle-muncher update handler). */
  idleMuncherElapsed = 0;
  /** Per-hazard last telegraph times (sim-ms key by hazard id) so the pre-step warning fires once/step. */
  hazardTelegraphAt = new Map<number, number>();
  /** Human-readable warning copy is throttled separately from the per-cell visual flash. */
  warningHintAt = new Map<string, number>();
  /** Final-5s tension: the last whole second we played the countdown SFX for (fire at most once/sec). */
  lastCountdownSec = -1;
  /** debug FPS overlay text. Public (`fps`) so capture.mjs + window.__game can hide it. */
  fpsText?: Phaser.GameObjects.Text;

  lastEventTime = -1; // EventLog.since() cursor (sim time ms)
  ended = false;
  /** Wall-clock handoff to Result; intentionally independent of scene.time.timeScale hitstop. */
  resultTransitionTimer: number | undefined;
  /** E1: set once after a per-frame throw so we log the crash exactly once, not every rAF frame. */
  frameErrorLogged = false;
  /** Set once a frame threw fatally: the rAF loop then no-ops so it can never re-throw (and pause can't
   *  re-enter the broken tick). The global recovery panel takes over the screen. */
  frameFatal = false;
  /** M3: pause state — freezes the sim tick + input dispatch while the pause menu is open. */
  paused = false;
  pauseLayer?: Phaser.GameObjects.Container;
  /** First human-facing board starts from a coach card so the rival never races under explanation. */
  preRoundActive = false;
  preRoundLayer?: Phaser.GameObjects.Container;
  // M2 audio/juice state
  /** last music-intensity we pushed (avoid spamming setIntensity every frame). */
  lastIntensity = -1;
  /**
   * SIGNATURE PEAK state (renderer-only). `threadsThisBoard` counts the player's threaded-gauntlet
   * eats this board so the 2nd+ thread escalates into the GAUNTLET CHAIN ×N crescendo (the 1st keeps
   * the existing THREADED IT beat). Resets per board in init().
   */
  threadsThisBoard = 0;
  /**
   * SIGNATURE PEAK re-gate (S5). `chainStreak` counts the player's CONSECUTIVE threaded eats — the
   * non-farmable measure the GAUNTLET CHAIN crescendo escalates on (decision 1a). Distinct from
   * `threadsThisBoard` (total threads this board): a decoy-eat resets `chainStreak` to 0 (see
   * eventFeedback munchDecoy), so the escalating crescendo tiers stay a PEAK, not wallpaper. Reset per
   * board in init().
   */
  chainStreak = 0;
  /**
   * DIRECTOR-RATIFIED (S5): `chainStreak` deliberately does NOT reset on a HAZARD hit — only on the
   * player's own decoy-eat (the farm-proof lever) and on board init. A hazard bump is bad luck, not a
   * routing mistake, so it must not also erase a clean chain the player earned. Documented here so the
   * reviewer stops re-flagging the "shouldn't a hazard break the chain?" question.
   */
  /**
   * The LIVE GAUNTLET CHAIN ×N stamp (item 2 fix): a fast chain (5/6/7 in quick succession) used to
   * stack a fresh stamp over the still-fading prior one — the "×4 5 +225" ghosted double-strike. We hold
   * a single ref and destroy the previous stamp before drawing the next so only the latest ×N reads.
   */
  chainStampObj?: Phaser.GameObjects.Container;
  /**
   * PERFECT GAUNTLET flag (S5 — the signature clip). Set when the board is won with zero decoys eaten,
   * ≥3 corrects, and EVERY correct threaded (see detectPerfectGauntlet). Drives the screen-consuming
   * board-end set-piece + the longer Result handoff + the Result share-card badge. Reset per board.
   */
  perfectGauntlet = false;
  /**
   * Music swell 0..1: a crescendo SLAMS this to 1 and it decays toward 0 over ~1200ms (driven by the
   * frame delta in tickFrame). updateMusicIntensity() floors the timer/hazard intensity with it and
   * force-pushes setIntensity while it's active, so a chain audibly peaks then settles back to the bed.
   */
  musicSwell = 0;
  /**
   * Anti-spam clock (sim-event ms) of the last FULL tier-3 chain crescendo. A tier-3 re-fire within
   * 600ms goes "quiet" (only the ×N stamp refreshes) so rapid threads don't stack hitstops/rings/risers.
   */
  lastChainSlowMoMs = -1;
  /**
   * Transient GauntletChain spectacle objects (hero stamp + spillover), tracked so any in-flight beat
   * is torn down cleanly on SHUTDOWN (board change / restart) — no leaked tweens. Entries self-prune on
   * their own tween onComplete; the shockwave ring + particles clean themselves inside the juice helpers.
   */
  chainSpectacleObjs = new Set<Phaser.GameObjects.GameObject>();
  /** throttle the near-miss tension cue so a lingering hazard doesn't strobe. */
  nextNearMissMs = 0;
  /** throttle the rival's cell-arrival pulse ring so a fast rival run doesn't spray rings. */
  nextRivalArrivalMs = 0;
  /** S6 rival board-token pose: the persistent BASE state (idle unless the rival leads/loses).
   *  Transient states (chomp) revert to this. Set by lead flips + board end; reset per board. */
  rivalBasePose: import('../game/unitsView').RivalPoseState = 'idle';
  /** throttle the scrambler shuffle SFX so a burst of swaps in one tick doesn't strobe audio. */
  nextScrambleSfxMs = 0;
  /** mirror lives so we can detect a life-lost transition for SFX. */
  prevLives = -1;
  /** mirror the human's cell so the death/respawn cue (VG-10) knows where the muncher WAS pre-reset. */
  prevHumanPos = -1;
  /**
   * VG-10: transient death/respawn-cue display objects (the death "ghost" + materialize ring), tracked
   * so any in-flight cue is torn down cleanly on SHUTDOWN (board change / restart) — no leaked tweens.
   * Only ever a couple of short-lived objects at a time; entries self-prune on their tween onComplete.
   */
  respawnCueObjs = new Set<Phaser.GameObjects.GameObject>();

  // touch
  pointerDown: { x: number; y: number; t: number } | null = null;
  /**
   * Queued single-step directions for the current pointer run — a straight-drag GLIDE (N repeats of
   * one direction) or a tap-anywhere AUTOPATH (a BFS route). Fed ONE 'move' per STEP_FEED_MS through
   * dispatch(); every step re-validates against LIVE Core state before firing, and any new
   * gesture/keypress replaces the whole queue.
   */
  pendingSteps: Direction[] = [];
  /** ms accumulator for the STEP_FEED_MS step pacer (advanced by the frame delta in tickFrame). */
  stepFeedAccumMs = 0;
  /** Scene-clock ms of the last pointer-driven move step — arms the own-cell tap->CHOMP mis-tap guard. */
  lastPointerMoveMs = -Infinity;
  /**
   * #41 KEYBOARD DAS/ARR. The movement Key objects (arrows + WASD), created lazily in pollHeldMovement.
   * Creating them ALSO makes Phaser suppress the OS auto-repeat on `keydown-*` (it emits those only when
   * `!key.isDown`), which is what killed the old "500ms stall then machine-gun" — the initial step still
   * fires via the keydown handler, then this poll owns the repeat at DAS(140)→ARR(90).
   */
  private moveKeys?: { key: Phaser.Input.Keyboard.Key; dir: Direction }[];
  /** #41: the direction currently held for auto-repeat. */
  private heldDir: Direction | null = null;
  /** #41: hold-time accumulator (real frame ms) for the DAS delay + ARR interval. */
  private heldMs = 0;
  /** #41: true once the DAS delay elapsed and we're auto-repeating at ARR. */
  private heldRepeating = false;
  /** #41: last frame's held movement dirs, to detect a FRESH press (whose step the keydown handler owns). */
  private prevDownDirs: Direction[] = [];
  /**
   * Idle-nudge (blind first-time-player review, 2026-07-01): novice/training boards carry little or no
   * timer/rival pressure, so a stalled brand-new player can stare at a silent board indefinitely.
   * Scene-clock time of the last human input (0 = arm on the next live frame) + per-board nudge cap.
   * The nudge text must NEVER reveal cell correctness (design pillar: no pre-reveal) — verbs only.
   */
  lastHumanInputMs = 0;
  idleNudgesShown = 0;
  /**
   * One-shot novice-only "race lost" notice: when the rival has eaten enough that quota is
   * unreachable, Core (correctly, deterministically) just runs the timer down — but a brand-new
   * player sits in unexplained silence (found by the 2026-07-01 first-session review). Training
   * boards are unranked teaching space, so naming the doomed state is coaching, not an info leak;
   * regular boards intentionally do NOT get this (remaining-correct count is hidden knowledge).
   */
  doomedNoticeShown = false;
  /** While `time.now` is before this, updateUnderText leaves the hint line alone (transient hint owns it). */
  hintHoldUntilMs = 0;
  /** Pooled overlay that previews the reachable glide path + its clamp cell while a straight drag is held. */
  glidePreview?: Phaser.GameObjects.Graphics;

  constructor() {
    super('Game');
  }

  /**
   * Capture/test hook surface: `window.__game.scene.fps` (capture.mjs probes `scene.fps.setVisible`).
   * Returns a tiny wrapper so the overlay can be hidden even when it was never created.
   */
  get fps(): { setVisible: (v: boolean) => void } {
    return { setVisible: (v: boolean) => this.setDebugVisible(v) };
  }

  /** Hide/show ONLY the debug FPS overlay (keeps the rest of the dev hooks live for the harness). */
  setDebugVisible(visible: boolean): void {
    this.fpsText?.setVisible(visible);
  }

  init(data: GameInit): void {
    const boot = readBoot(this);
    this.seed = data.seed ?? boot.seed;
    this.topic = data.topic ?? boot.topic ?? '';
    this.pendingContent = data.content ?? null;
    this.topicLabel = data.topicLabel ?? '';
    this.isDaily = data.isDaily ?? boot.daily;
    // Daily twist: prefer the mutator STAMPED in a deep link (&m=N — a cross-day open replays the
    // exact rules/layout the sharer's target + ghost were set on) over today's date pick. Resolved
    // ONCE so params, the pre-round eyebrow, and share minting can never split across a UTC rollover.
    this.dailyMutatorId = this.isDaily
      ? boot.dailyMutator >= 0 && boot.dailyMutator < DAILY_MUTATORS.length
        ? boot.dailyMutator
        : mutatorForDay().id
      : -1;
    this.peakCombo = 0;
    this.prevScore = -1;
    this.prevToGo = -1; // reset the near-quota buildup change-detector each board (scene is reused across boards)
    this.lastHumanInputMs = 0; // idle-nudge re-arms per board
    this.idleNudgesShown = 0;
    this.doomedNoticeShown = false;
    this.hintHoldUntilMs = 0;
    this.underTextYieldUntilMs = 0;
    this.commandHintBg = undefined;
    this.scanTeachBubble = undefined; // scene objects died on SHUTDOWN; drop the stale handle
    this.scanBubbleRetried = false;
    this.aiEnabled = data.ai ?? boot.ai;
    this.rivalWarmup = data.rivalWarmup === true;
    this.boardIndex = data.boardIndex ?? 0;
    // Board 0 is the start of a match (organic warmup, direct ?ai=, NEW MATCH relaunch, ladder, series
    // game — every launcher lands here at index 0). Re-arm BYT3's grudge cold-open so it fires once on his
    // JOIN board (see rivalPersona.reactBoardStart); NEXT boards (index ≥ 1) leave the flag alone.
    if (this.boardIndex === 0) {
      resetGrudgeColdOpen(this);
      resetBoutIntro(this); // STRETCH A1: re-arm the bout intro so it fires once, on the rival's join board
    }
    this.forceDeception = data.forceDeception === true;
    this.debug = boot.debug;
    this.boardSource = data.boardSource ?? 'unknown';
    this.noviceRun = data.noviceRun === true;
    this.challengeTarget = Math.max(0, Math.floor(data.challengeTarget ?? 0));
    this.ghostOutcomes = decodeGhost(data.challengeGhost);
    this.ghostMarkers = new Map();
    // `||` (not `??`): startWithContent passes an EMPTY-STRING ghostSource for a normal daily, which
    // would shadow a `?raceghost=1` boot param under `??`; empty-string must fall through to boot.
    this.ghostSource = data.ghostSource || boot.ghostSource;
    // GHOST DUELS Slice B: the &gr deep-link ghost id (renderer-only, boot-scoped). Gated at fetch time on
    // isDaily + board-0 + the blob pin — a live remote replay when it all matches, else static footprints.
    this.ghostRemoteId = boot.ghostRemoteId;
    // GHOST DUELS Slice C: the ladder challenger to duel (record + rung), carried from the panel launch. Only
    // ever set on the daily board-0 launch; NEXT boards start without it. resolvePendingGhost() null-checks
    // the pin below and clears this to null on a miss so ResultScene never shows a phantom rung verdict.
    this.ladderRace = data.ladderGhost ?? null;
    this.ghostRacer = undefined; // the prior racer was destroyed on SHUTDOWN
    this.pendingGhostTrack = null;
    this.ghostName = 'YOUR GHOST';
    this.ghostRecorded = false;
    this.series = data.series ?? null;
    this.scanUnlocked = true;
    this.timerVisible = true;
    this.boardStartSimMs = 0;
    this.boardTerminalTracked = false;
    // FPS overlay is now gated behind its OWN flag (?fps=1), NOT ?debug=1 — so the capture harness
    // (which boots with ?debug=1 to get the hooks) never bakes the FPS text into a screenshot.
    this.showFps = new URLSearchParams(window.location.search).get('fps') === '1';
    this.introForced = new URLSearchParams(window.location.search).get('intro') === '1';
    this.taughtArm = false;
    this.taughtWrongScan = false;
    this.taughtEarlyScan = false;
    this.firstBiteShown = false;
    this.taughtNoviceThread = false;
    this.ended = false;
    this.paused = false;
    this.pauseLayer = undefined;
    this.preRoundActive = false;
    this.preRoundLayer = undefined;
    this.safePassBanner = undefined;
    this.safePassBannerText = undefined;
    this.safePassBannerBg = undefined;
    this.safePassCount = 0;
    this.safePassResetTimer = undefined;
    this.primedFieldCueShown = false;
    this.lastEventTime = -1;
    if (this.resultTransitionTimer !== undefined) window.clearTimeout(this.resultTransitionTimer);
    this.resultTransitionTimer = undefined;
    this.cellViews = [];
    // S3 item 8: the board-clip mask is rebuilt per board (the grid rect can change); the old Graphics
    // dies with the scene on SHUTDOWN, so just drop the stale references here.
    this.boardClipGfx = undefined;
    this.boardClipMask = undefined;
    this.hazardSprites = new Map();
    this.hazardAuraTweens = new Map();
    this.lastIntensity = -1;
    this.threadsThisBoard = 0;
    this.chainStreak = 0;
    this.perfectGauntlet = false;
    this.musicSwell = 0;
    this.lastChainSlowMoMs = -1;
    this.chainStampObj = undefined;
    this.chainSpectacleObjs = new Set();
    this.nextNearMissMs = 0;
    this.nextRivalArrivalMs = 0;
    this.nextScrambleSfxMs = 0;
    this.prevLives = -1;
    this.prevHumanPos = -1;
    this.respawnCueObjs = new Set();
    this.prevEatenCorrect = -1;
    this.lastCountdownSec = -1;
    // WARN-1 rival readout: drop any prior references + reset its change-detect mirrors so a fresh
    // board rebuilds the chip cleanly (the old objects are destroyed with the scene on SHUTDOWN).
    this.rivalReadout = undefined;
    this.rivalReadoutBg = undefined;
    this.rivalThinkingText = undefined; // rebuilt with the readout; rivalHasActed/graceMs re-set in create()
    this.rivalPersona = undefined; // rebuilt in create() (its face object dies with the scene)
    this.broadcast = undefined; // STRETCH A: rebuilt in create() for a non-ladder rival match (dies with the scene)
    this.rivalScoreText = undefined;
    this.aiPressureCue = undefined;
    // Tactical-OLED HUD refs (rebuilt per board in buildHud) + the held tension state.
    this.heartRowC = undefined;
    this.heartRowSet = undefined;
    this.comboSubLabel = undefined;
    this.timerPillC = undefined;
    this.timerPillSet = undefined;
    this.tensionVeil = undefined;
    this.tensionOn = false;
    this.tensionWarmth = undefined;
    this.heartGs = [];
    this.heartTensionOn = false;
    this.tensionHeartTween = undefined;
    this.timerGlow = undefined;
    this.timerGlowTween = undefined;
    this.timerPillPulse = undefined;
    this.timerLowOn = false;
    this.timerThrobTween = undefined;
    this.playerTrailEmitter = undefined;
    this.aiTrailEmitter = undefined;
    this.surgeSparks = undefined;
    this.prevRivalScore = -1;
    this.prevLead = 0;
    this.prevLeadSign = 0;
    this.underTextBg = undefined;
    this.quotaPips = [];
    this.bannerBg = undefined;
    this.bannerRule = undefined;
    this.boardDepth = undefined;
    this.boardDepthElapsed = 0;
    this.idleSheens = [];
    this.idleSheenElapsed = 0;
    this.idleMunchers = [];
    this.idleMuncherElapsed = 0;
    this.hazardTelegraphAt = new Map();
    this.warningHintAt = new Map();
    // #41 keyboard DAS/ARR held-repeat state — reset per run. The Key objects are re-fetched lazily in
    // pollHeldMovement (the keyboard plugin clears its own keys on shutdown, so no stale refs carry over).
    this.moveKeys = undefined;
    this.heldDir = null;
    this.heldMs = 0;
    this.heldRepeating = false;
    this.prevDownDirs = [];
    this.underTextDimmed = false;
    // Gap fix: free the shared caption slot at every new-board start so a caption queued during the
    // prior board→Result handoff can never stall this board's barks/taunts (defensive; the slot's own
    // shutdown listener now also resets on every board).
    resetCaptionSlot(this);
    // GHOST DUELS Slice A: resolve the local self-race ghost (bv/seed/mutator/topic-pinned) after all
    // daily identity fields above are set. Fail-closed: any mismatch/corruption leaves it null (no ghost).
    this.resolvePendingGhost();
  }

  // --- Ghost controller (extracted; see game/ghostController.ts) ---
  resolvePendingGhost = Ghost.resolvePendingGhost;
  fetchRemoteGhost = Ghost.fetchRemoteGhost;
  buildGhostRacer = Ghost.buildGhostRacer;
  recordDailyGhost = Ghost.recordDailyGhost;


  create(): void {
    // M1: prefer a board the Menu handed us (generated or seed); otherwise fall back to the M0 path
    // of looking the topic up in the seed pack. SEED_BOARDS[0] is the last-resort default.
    const content =
      this.pendingContent ??
      (this.noviceRun ? noviceBoardFor(this.boardIndex) : null) ??
      getSeedBoard(this.topic) ??
      SEED_BOARDS[0];
    if (this.boardSource === 'unknown') this.boardSource = boardSourceFromContent(content);
    // M3: lock in a human-facing label for the results/share/leaderboard (prefer an explicit label,
    // else the board's raw topic, else the slug).
    if (!this.topicLabel) this.topicLabel = content.topicRaw || content.topicNormalized || this.topic;
    // Daily ("Today's Gauntlet") gets its own fenced-but-fair profile so the front-door board actually
    // contains the Gauntlet — not the unfenced boardIndex-0 curve it used to inherit. Covers the menu
    // hero, the ResultScene streak-chip replay, and ?daily=1.
    // 6×6 on tablet/desktop (handoff responsive), 5×6 on phone — a renderer breakpoint passed as the
    // Core `base` grid (no Core/Data change). Daily stays on the phone default so the SHARED daily
    // leaderboard compares like-for-like across device classes; the tutorial keeps its own small grids.
    const gridBase = gridForViewport();
    const params = this.noviceRun
      ? noviceDifficultyForBoard(this.boardIndex)
      : this.isDaily
        ? dailyDifficulty(undefined, undefined, this.dailyMutatorId)
        : difficultyForBoard(this.boardIndex, gridBase, undefined, this.forceDeception ? true : undefined);
    this.scanUnlocked = !this.noviceRun || isNoviceScanUnlocked(this.boardIndex);
    this.timerVisible = !this.noviceRun || this.boardIndex > 0;
    // Rival "thinking" tell (feel-retune review MINOR): during the AI's board-start grace it makes no
    // move, which can read as absent — surface a subtle "…" on its readout until its first act.
    this.rivalGraceMs = AI_TIERS[params.aiTier].startGraceMs;
    this.rivalHasActed = false;
    this.rivalPersona = new RivalPersona(this, this.aiEnabled);
    // Per-board seed mirror of Match.makeSim so board N+1 differs from board 0 deterministically.
    // #37: the novice FENCED board (index 1) must present ≥1 fenced correct so the mandatory-path
    // THREADED IT can fire — but the default match-seed derivation lands a fence-free layout on ~44% of
    // seeds (empirically 0 fenced at the default seed 11). noviceBoardSeed swaps board 1 to a pre-vetted,
    // deterministic seed proven (novicefence:smoke) to fence ≥1 correct at NOVICE_PARAMS[1].fenceRatio=
    // 0.45; every other board keeps the standard per-board derivation. Render-side only (the Sim seed
    // input) — src/core is untouched.
    const boardSeed = this.noviceRun
      ? noviceBoardSeed(this.boardIndex, this.seed)
      : (this.seed ^ Math.imul(this.boardIndex + 1, 0x9e3779b1)) >>> 0;
    this.sim = new Sim({ content, params, seed: boardSeed, aiEnabled: this.aiEnabled });

    const snap = this.sim.snapshot();
    this.boardStartSimMs = snap.timeMs;
    track('board_started', this.analyticsDims(snap));
    this.rows = snap.rows;
    this.cols = snap.cols;
    this.timerMaxMs = Math.max(1, snap.timeLeftMs);

    this.cameras.main.setBackgroundColor(PALETTE.bg);
    // VG-11: paint the atmospheric backdrop (gradient/bloom/grid at depth -10) SYNCHRONOUSLY here,
    // before any board build/entrance, so the FIRST composited frame already shows the full backdrop —
    // never the bare navy camera-bg. addBackdrop builds all its layers inline (no async/deferred paint),
    // so by the time create() returns the screen is fully dressed behind the grid.
    addBackdrop(this, 'game');
    // PER-BOARD ATMOSPHERE: a static, deterministic hue wash over the shared backdrop so board 3
    // does not read identical to board 1 (Gemini P1). Painted synchronously (like the backdrop) at
    // depth -9.5 — above the backdrop (-10), below the tension warmth (0.4) and every board/HUD
    // layer — so the first composited frame already carries the rung's mood. Decoration only.
    {
      const theme = this.boardTheme();
      this.add
        .rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, theme.wash, theme.washAlpha)
        .setDepth(-9.5)
        .setScrollFactor(0);
    }
    // VG-11: cover the one-frame scene-start seam (where only the camera bg has composited) with a short
    // dark fade-in — the SAME boot-seam treatment ResultScene already uses. It is NOT a second board
    // reveal: the board-entrance cascade pops cells from scale (it doesn't fade from the camera bg), so
    // this fade only hides the blank-blue gap and resolves into the already-cascading board. Motion-gated,
    // and suppressed under the capture harness (?debug=1) — like runBoardReveal — so a create()-time
    // screenshot is never caught mid-fade and stays deterministic.
    if (!this.debug && this.boardIndex === 0 && motionAllowed()) this.cameras.main.fadeIn(240, 8, 10, 18);
    this.computeLayout();
    // PER-RUNG MOTIF FIELD: painted right after layout (it needs the board rect to stay clear of
    // the tiles) but still under everything above depth -9.4 — see paintBoardMotif's contract.
    this.paintBoardMotif();
    this.buildBanner(snap);
    this.buildGrid(snap);
    // P1.2b: a friend's ghost route (faint markers of what they ate/threaded) on a challenge board.
    // Keep the returned marker map so the footprints RETIRE one-way as the player reaches each cell
    // (pruneGhostMarkers, per frame) and tear down cleanly on SHUTDOWN — previously the map was discarded.
    this.ghostMarkers = buildGhostOverlay(this, this.ghostOutcomes, this.cellViews.length, this.gridX, this.gridY, this.cellW, this.cellH, this.cols);
    this.buildMunchers(snap);
    this.buildHud(snap);
    // STRETCH Lane A — BROADCAST PACKAGE. Scoped to a live BYT3 match (a ladder duel is a ghost, not the
    // rival, so it keeps its own framing). Builds the RIVAL PRESSURE bar + the (hidden) menace edge; the
    // once-per-match lower-third bout intro is scheduled past the pre-round coach so it never renders
    // under the coach veil. Pure Snapshot consumer — no Core / AI-internal reads.
    if (this.aiEnabled && this.ladderRace === null) {
      this.broadcast = new Broadcast(this);
      this.broadcast.build(snap);
      const rivalTier = params.aiTier;
      this.time.delayedCall(BROADCAST.boutIntroDelayMs, () => this.broadcast?.tryShowBoutIntro(rivalTier));
    }
    this.buildGhostRacer(snap);
    // GHOST DUELS Slice B: no LOCAL ghost resolved but a &gr id present → fetch the friend's KV ghost and
    // build the racer async when its pin matches (fail-closed to the static-footprint path otherwise).
    if (!this.ghostRacer) this.fetchRemoteGhost(snap);
    this.bindInput();

    // Only the explicit ?fps=1 flag draws the overlay (?debug=1 still installs the dev hooks but no
    // visible FPS text, keeping captures clean by default). Still individually hideable via the hook.
    if (this.showFps) {
      this.fpsText = this.add
        .text(6, 6, '', { fontFamily: FONT.mono, fontSize: '12px', color: hex(PALETTE.textDim) })
        .setDepth(11_000)
        .setScrollFactor(0);
    }

    // First full render so a screenshot at create() is correct & deterministic.
    this.renderSnapshot(snap, true);

    // M2 AUDIO + board-reveal "cooking" animation. The bus is a no-op in headless captures (no
    // gesture -> suspended context), and boardReveal collapses to a quick fade under reduced-motion,
    // so the create()-time screenshot stays legible/deterministic.
    audio().attach(this);
    audio().playGameplayMusic();
    audio().playVo('voGetReady');
    audio().playSfx('countdown', { volume: 0.8 });
    // Board-reveal flourish is suppressed under the capture harness so screenshots are deterministic.
    if (!this.debug) this.runBoardReveal();
    const human0 = snap.players.find((p) => p.id === PLAYER);
    this.prevLives = human0?.lives ?? -1;
    this.prevHumanPos = human0?.pos ?? -1;
    // The pre-round topic flash shows in normal play; ?intro=1 also forces it under the debug/capture
    // path so social-replay clips can include it (deterministic screenshots, debug-only, stay clean).
    if (!this.debug || this.introForced) this.openPreRoundCoach(snap);
    // Debug/capture path skips the pre-round coach (and with it startRound's persona hook) — fire
    // BYT3's opener directly so evidence captures include the persona a real player gets.
    else if (!this.noviceRun) this.time.delayedCall(2300, () => this.rivalPersona?.reactBoardStart(this.ladderRace !== null));

    // Point window.__game at this live scene/sim.
    setActiveGameScene(this);
    this.events.emit('game-ready');

    // VG-7: kill any lingering hazard threat-aura pulse tweens on scene shutdown so none leak across a
    // board change / restart (the aura discs are children of the hazard containers and die with them).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.trackBoardAbandoned();
      // Two-touch-input teardown: no pending glide/autopath steps leak into a dead scene.
      this.cancelPendingSteps();
      for (const [id] of this.hazardAuraTweens) this.removeHazardDangerAura(id);
      // VG-10: tear down any in-flight death/respawn cue so a mid-cue board change leaks nothing.
      for (const o of this.respawnCueObjs) {
        this.tweens.killTweensOf(o);
        o.destroy();
      }
      this.respawnCueObjs.clear();
      // Signature-peak spectacle (GAUNTLET CHAIN hero stamp etc.) — tear down any in-flight beat so a
      // mid-crescendo board change leaks nothing. The shockwave ring/particles self-clean in juice.ts.
      for (const o of this.chainSpectacleObjs) {
        this.tweens.killTweensOf(o);
        o.destroy();
      }
      this.chainSpectacleObjs.clear();
      this.chainStampObj = undefined;
      // A1 comet trails: stop + destroy the pooled per-muncher emitters so no particle stream (or a
      // pending tween-complete stop) leaks across a board change / restart.
      if (this.playerTrailEmitter?.active) this.playerTrailEmitter.destroy();
      if (this.aiTrailEmitter?.active) this.aiTrailEmitter.destroy();
      this.playerTrailEmitter = undefined;
      this.aiTrailEmitter = undefined;
      // GHOST DUELS Slice A: retire the challenge-ghost footprints + the self-race replay entity so no
      // marker tween / ghost object (or its chip) leaks across a board change or restart.
      destroyGhostMarkers(this, this.ghostMarkers);
      this.ghostRacer?.destroy();
      this.ghostRacer = undefined;
      // VG-12: wipe any in-flight score floats too, so the module-level activeFloatCards set never
      // holds a destroyed Container ref after this scene shuts down (their onComplete won't fire once
      // the tween is killed on shutdown).
      clearActiveFloats(this);
      if (this.resultTransitionTimer !== undefined) {
        window.clearTimeout(this.resultTransitionTimer);
        this.resultTransitionTimer = undefined;
      }
    });
  }

  // --- Board controller (extracted; see game/boardView.ts) ---
  runBoardReveal = Board.runBoardReveal;
  computeLayout = Board.computeLayout;
  buildBanner = Board.buildBanner;
  boardRung = Board.boardRung;
  boardTheme = Board.boardTheme;
  boardWorldBucket = Board.boardWorldBucket;
  boardWorldAccent = Board.boardWorldAccent;
  paintBoardWorld = Board.paintBoardWorld;
  paintBoardMotif = Board.paintBoardMotif;
  paintFrameCornerMotif = Board.paintFrameCornerMotif;
  buildGrid = Board.buildGrid;
  installIdleBoardLife = Board.installIdleBoardLife;
  paintTileGel = Board.paintTileGel;
  setCellLabel = Board.setCellLabel;
  cellCenter = Board.cellCenter;
  applyLabelOcclusion = Board.applyLabelOcclusion;
  renderCell = Board.renderCell;
  setLieTell = Board.setLieTell;
  playScrambleSwap = Board.playScrambleSwap;
  crunchCorrectCell = Board.crunchCorrectCell;
  tintBg = Board.tintBg;
  drawStateFrame = Board.drawStateFrame;
  setGlyphCorner = Board.setGlyphCorner;
  setTileArt = Board.setTileArt;
  applyCellMode = Board.applyCellMode;
  playVfx = Board.playVfx;
  ensureBoardClipMask = Board.ensureBoardClipMask;
  hazardTelegraph = Board.hazardTelegraph;
  pulseScanReveal = Board.pulseScanReveal;

  // --- Units controller (extracted; see game/unitsView.ts) ---
  buildMunchers = Units.buildMunchers;
  registerIdleMuncher = Units.registerIdleMuncher;
  startMuncherIdle = Units.startMuncherIdle;
  munchRadius = Units.munchRadius;
  spriteYOffset = Units.spriteYOffset;
  addSpriteShadow = Units.addSpriteShadow;
  addSeparationGlow = Units.addSeparationGlow;
  addRivalIntentPulse = Units.addRivalIntentPulse;
  makeArtSprite = Units.makeArtSprite;
  fitImageTo = Units.fitImageTo;
  setSpriteFrame = Units.setSpriteFrame;
  pulseSpriteFrame = Units.pulseSpriteFrame;
  pulseMoveSpriteFrame = Units.pulseMoveSpriteFrame;
  pulseMoveSquash = Units.pulseMoveSquash;
  muncherBitePop = Units.muncherBitePop;
  attachMuncherSprite = Units.attachMuncherSprite;
  playMuncherAnim = Units.playMuncherAnim;
  restoreMuncherTint = Units.restoreMuncherTint;
  reactMuncher = Units.reactMuncher;
  makeHumanSprite = Units.makeHumanSprite;
  makeAiSprite = Units.makeAiSprite;
  rivalPosesReady = Units.rivalPosesReady;
  attachRivalPoseSprite = Units.attachRivalPoseSprite;
  fitRivalPose = Units.fitRivalPose;
  setRivalPose = Units.setRivalPose;
  addPlayerTag = Units.addPlayerTag;
  hazardKeyFor = Units.hazardKeyFor;
  makeHazardSprite = Units.makeHazardSprite;
  addHazardDangerAura = Units.addHazardDangerAura;
  removeHazardDangerAura = Units.removeHazardDangerAura;
  makeProceduralHazard = Units.makeProceduralHazard;
  playMoveStep = Units.playMoveStep;
  moveSprite = Units.moveSprite;
  pulseRivalArrival = Units.pulseRivalArrival;
  showRivalMoveTelegraph = Units.showRivalMoveTelegraph;
  trailEmitterFor = Units.trailEmitterFor;
  emitMoveTrail = Units.emitMoveTrail;
  updateAiPressureCue = Units.updateAiPressureCue;
  showRivalMunchBeat = Units.showRivalMunchBeat;
  playRespawnCue = Units.playRespawnCue;
  isHumanProtected = Units.isHumanProtected;
  updatePlayerAuras = Units.updatePlayerAuras;
  ensureSurgeSparkTexture = Units.ensureSurgeSparkTexture;
  updateSurgeSparks = Units.updateSurgeSparks;

  // --- Hud controller (extracted; see game/hudController.ts) ---
  buildHud = Hud.buildHud;
  buildQuotaChip = Hud.buildQuotaChip;
  updateQuotaChip = Hud.updateQuotaChip;
  buildRivalReadout = Hud.buildRivalReadout;
  updateRivalReadout = Hud.updateRivalReadout;
  updateWinStatus = Hud.updateWinStatus;
  buildCommandButton = Hud.buildCommandButton;
  pulseDockButton = Hud.pulseDockButton;
  buildPauseButton = Hud.buildPauseButton;
  updateHud = Hud.updateHud;
  updateLegalMoveHints = Hud.updateLegalMoveHints;
  setTensionState = Hud.setTensionState;
  updateHeartTension = Hud.updateHeartTension;
  updateTimerLowState = Hud.updateTimerLowState;
  pulseComboReadout = Hud.pulseComboReadout;
  pulseScoreReadout = Hud.pulseScoreReadout;
  flashScoreNegative = Hud.flashScoreNegative;
  showComboTierMilestone = Hud.showComboTierMilestone;
  updateTimerTension = Hud.updateTimerTension;
  updateUnderText = Hud.updateUnderText;
  updateSelectionRing = Hud.updateSelectionRing;
  showCommandHint = Hud.showCommandHint;
  showWarningHint = Hud.showWarningHint;
  showSafePassBanner = Hud.showSafePassBanner;
  dismissSafePassPill = Hud.dismissSafePassPill;
  showPrimedFieldCue = Hud.showPrimedFieldCue;

  // --- Input controller (extracted; see game/inputController.ts) ---
  bindInput = Input.bindInput;
  playerKey = Input.playerKey;
  onPointerUp = Input.onPointerUp;
  beginGlide = Input.beginGlide;
  beginPathWalk = Input.beginPathWalk;
  cancelPendingSteps = Input.cancelPendingSteps;
  releasePendingStep = Input.releasePendingStep;
  onPointerMove = Input.onPointerMove;
  drawGlidePreview = Input.drawGlidePreview;
  clearGlidePreview = Input.clearGlidePreview;
  cellIdAt = Input.cellIdAt;
  pathDirsTo = Input.pathDirsTo;

  // --- Analytics controller (extracted; see game/analyticsBridge.ts) ---
  analyticsDims = Analytics.analyticsDims;
  trackBoardCompleted = Analytics.trackBoardCompleted;
  maybeIdleNudge = Analytics.maybeIdleNudge;
  trackBoardAbandoned = Analytics.trackBoardAbandoned;


  /** Single funnel: every input source routes through here -> window.__game.input parity. */
  dispatch(cmd: InputCmd): void {
    if (this.ended || this.paused) return;
    this.lastHumanInputMs = this.time.now; // any command (even a rejected one) proves the player is engaged
    if (this.preRoundActive) {
      this.startRound();
      return;
    }
    if (cmd === 'munch') {
      const snap = this.sim.snapshot();
      const human = snap.players.find((p) => p.id === PLAYER);
      const here = human ? snap.cells[human.pos] : undefined;
      if (!here || here.eaten || here.kind === 'wall') {
        this.showCommandHint('MOVE TO A FRESH TILE', PALETTE.textDim, 1200);
        return;
      }
      this.sim.enqueue(PLAYER, { type: 'munch' });
      trackOnce('first_chomp', 'first_chomp', this.analyticsDims(this.sim.snapshot()));
    } else if (cmd === 'scan') {
      if (!this.scanUnlocked) {
        this.showCommandHint('SCAN unlocks after the first training board.', PALETTE.textDim);
        return;
      }
      const human = this.sim.snapshot().players.find((p) => p.id === PLAYER);
      if (!human || human.scansLeft <= 0) {
        this.showCommandHint('NO SCANS LEFT', PALETTE.textDim, 1200);
        return;
      }
      this.sim.enqueue(PLAYER, { type: 'scan' });
    } else {
      this.sim.enqueue(PLAYER, { type: 'move', dir: cmd });
      trackOnce('first_move', 'first_move', this.analyticsDims(this.sim.snapshot()));
    }
  }

  /**
   * #41 KEYBOARD DAS/ARR (render-side; sim semantics untouched). Advertised feel = DAS 140 → ARR 90,
   * which never actually existed: with no Key object registered for the movement codes, Phaser emitted
   * `keydown-UP` on every OS auto-repeat, so holding a key did nothing until ~500ms then machine-gunned
   * at the OS rate. Registering the Keys here fixes BOTH ends: Phaser now emits `keydown-*` only on the
   * true first press (repeat = key.isDown), killing the OS machine-gun, and we get `.isDown` to poll. The
   * first step is still the keydown handler's; after DAS(140ms) of continuous hold we feed one 'move' per
   * ARR (STEP_FEED_MS = 90) through the SAME dispatch() funnel every input uses. A pointer run owns the
   * STEP_FEED queue, so we stand down (and re-arm DAS) while a drag glide / autopath is in flight.
   */
  private pollHeldMovement(delta: number): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    if (!this.moveKeys) {
      const KC = Phaser.Input.Keyboard.KeyCodes;
      this.moveKeys = [
        { key: kb.addKey(KC.UP), dir: 'up' as Direction },
        { key: kb.addKey(KC.W), dir: 'up' as Direction },
        { key: kb.addKey(KC.DOWN), dir: 'down' as Direction },
        { key: kb.addKey(KC.S), dir: 'down' as Direction },
        { key: kb.addKey(KC.LEFT), dir: 'left' as Direction },
        { key: kb.addKey(KC.A), dir: 'left' as Direction },
        { key: kb.addKey(KC.RIGHT), dir: 'right' as Direction },
        { key: kb.addKey(KC.D), dir: 'right' as Direction },
      ];
    }
    // A pointer run (drag glide / autopath) owns the STEP_FEED funnel — don't drive keyboard repeats over
    // it; clear held state so a still-held key re-arms the DAS delay once the run finishes.
    if (this.pendingSteps.length > 0) {
      this.heldDir = null;
      this.heldRepeating = false;
      this.prevDownDirs = [];
      return;
    }
    const down: Direction[] = [];
    for (const mk of this.moveKeys) if (mk.key.isDown && !down.includes(mk.dir)) down.push(mk.dir);
    const newlyDown = down.filter((d) => !this.prevDownDirs.includes(d));
    this.prevDownDirs = down;
    if (newlyDown.length > 0) {
      // Fresh press: the keydown handler already fired step 1 this frame — start the DAS clock, never
      // double-fire here. The latest new press wins so a direction change re-aims the repeat.
      this.heldDir = newlyDown[newlyDown.length - 1] ?? null;
      this.heldMs = 0;
      this.heldRepeating = false;
      return;
    }
    if (this.heldDir && !down.includes(this.heldDir)) {
      // The active held key was released — resume another still-held dir (re-arm DAS) or stop entirely.
      this.heldDir = down.length > 0 ? (down[down.length - 1] ?? null) : null;
      this.heldMs = 0;
      this.heldRepeating = false;
    }
    if (!this.heldDir) return;
    this.heldMs += delta;
    if (!this.heldRepeating) {
      if (this.heldMs >= KEY_DAS_MS) {
        this.heldRepeating = true;
        this.heldMs = 0;
        this.dispatch(this.heldDir); // first auto-repeat step, after the DAS delay
      }
    } else if (this.heldMs >= STEP_FEED_MS) {
      this.heldMs -= STEP_FEED_MS; // ARR cadence == STEP_FEED_MS (90)
      this.dispatch(this.heldDir);
    }
  }

  /**
   * Parity-capture harness hook (hooks.ts forwards window.__game cheats here). Manipulates the REAL
   * sim/board state, then leans on the EXISTING tickFrame board-end → scene.start('Result', …) path so
   * the Result scene renders the genuine win / loss / match-complete UI — never a hand-built payload.
   *   tension  : last-life + a low timer so the HUD-TENSION state renders (board keeps playing).
   *   endWin   : human meets quota + out-scores the rival → board WIN → Result.
   *   endLoss  : the rival edges it (or solo time/lives out) → board LOSS → Result.
   *   matchEnd : resolve the FINAL (5th) board as a win → Result shows the 5-board MATCH-COMPLETE.
   */
  applyCheat(name: string): void {
    if (!this.sim) return;
    switch (name) {
      case 'tension': {
        const human = this.sim.players[PLAYER];
        if (human) {
          human.lives = 1;
          human.scansLeft = 0; // mockup HUD-TENSION also shows the SCAN dock depleted (×0)
        }
        // 4s left is inside BOTH tension (<25%) and the timer LOW state (<15%), so the capture shows
        // the solid-hazard fill + glow + pulse alongside the last-life heart and warm backdrop.
        this.sim.timeLeftMs = Math.min(this.sim.timeLeftMs, 4000);
        // Re-render so the HUD-TENSION state (hearts/timer/vignette/scan-dock) reflects immediately.
        this.renderSnapshot(this.sim.snapshot(), true);
        this.setTensionState(true); // force the near-miss vignette on for the captured state
        break;
      }
      case 'endWin':
        this.forceBoardEnd('win');
        break;
      case 'endLoss':
        this.forceBoardEnd('loss');
        break;
      case 'endPhoto': {
        // FACE & FURY S5 evidence: a board-SETUP cheat that lands a genuinely TIGHT final margin so the
        // photo-finish beat fires from its REAL detection condition (|human−rival| ≤ PHOTO_FINISH_MARGIN).
        // The human edges the rival by ~80 pts (a nail-biter WIN); the detector — not this cheat — decides.
        const human = this.sim.players[PLAYER];
        const rival = this.sim.players[AI];
        if (human && rival) {
          const marked = this.markCorrectCellsForCapture(PLAYER, this.sim.state.quota);
          human.eatenCorrect = Math.max(human.eatenCorrect, marked, this.sim.state.quota);
          human.combo = Math.max(human.combo, marked);
          human.score = Math.max(human.score, 12480);
          rival.eatenCorrect = Math.max(rival.eatenCorrect, this.sim.state.quota - 1);
          rival.score = human.score - 80; // margin 80 ≤ 150 → a genuine photo finish
          this.sim.quotaMetBy = PLAYER;
          this.sim.winner = PLAYER;
          this.sim.status = 'won';
        }
        break;
      }
      case 'matchEnd':
        // The Result scene shows MATCH-COMPLETE on the final board (boardIndex >= 4). Seed the debug
        // fixture's prior run ledger first so the screenshot represents a truthful 5-board match.
        this.boardIndex = 4;
        this.seedCaptureRunLedger();
        this.forceBoardEnd('win');
        break;
      case 'rivalAhead': {
        // FACE & FURY S7b villain-layer evidence: put BYT3 clearly ahead on a LIVE board (no status/quota
        // change, so play continues) → the music director reads the real margin and swells the villain
        // bassline. The RAMP (director → setVillainPresence → tickFade) is the real wiring; this only
        // creates the lead. Reverted by 'humanAhead'.
        const human = this.sim.players[PLAYER];
        const rival = this.sim.players[AI];
        if (human && rival) {
          rival.score = human.score + 320;
          this.renderSnapshot(this.sim.snapshot(), true);
        }
        break;
      }
      case 'humanAhead': {
        // Companion to 'rivalAhead': the human retakes a clear lead → the villain layer fades back to 0.
        const human = this.sim.players[PLAYER];
        const rival = this.sim.players[AI];
        if (human && rival) {
          human.score = rival.score + 320;
          this.renderSnapshot(this.sim.snapshot(), true);
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Capture/dev terminal cheats must leave the Result screen with visible facts that agree with its
   * counters. Result accuracy and mastery read final cells, not just PlayerState counters; if a cheat
   * only bumps `eatenCorrect`, screenshots show impossible states such as 12,900 points at 0% accuracy.
   * This writes the minimum human/rival cell evidence needed for a coherent terminal screenshot.
   * Dev-hook only; real gameplay still reaches these fields through Core rules/events.
   */
  markCorrectCellsForCapture(playerId: number, desiredCount: number): number {
    const target = Math.max(0, Math.min(Math.floor(desiredCount), this.sim.state.correctCount));
    let owned = 0;
    for (const c of this.sim.state.cells) {
      if (c.kind === 'correct' && c.eaten && c.eatenBy === playerId) owned += 1;
    }
    if (owned >= target) return owned;
    for (const c of this.sim.state.cells) {
      if (owned >= target) break;
      if (c.kind !== 'correct' || c.eaten) continue;
      c.eaten = true;
      c.eatenBy = playerId;
      c.armed = false;
      c.armedUntilMs = 0;
      owned += 1;
    }
    return owned;
  }

  /**
   * Force the current board to a terminal status with the given outcome by writing the SAME fields
   * Sim.checkEnd() would set (status/winner + the deciding scores/quota). The next update()→tickFrame()
   * sees `snap.status !== 'playing'` and fires the real end-of-board → Result transition. No fake payload.
   */
  forceBoardEnd(outcome: 'win' | 'loss'): void {
    if (this.ended) return;
    const human = this.sim.players[PLAYER];
    const rival = this.sim.players[AI];
    if (!human) return;
    if (outcome === 'win') {
      // Non-degenerate, clear win (so the captured Result reads like a real board, not 1000-vs-0).
      const marked = this.markCorrectCellsForCapture(PLAYER, this.sim.state.quota);
      human.eatenCorrect = Math.max(human.eatenCorrect, marked, this.sim.state.quota);
      human.combo = Math.max(human.combo, marked);
      human.score = Math.max(human.score, 12480);
      if (rival) rival.score = Math.min(rival.score || 9210, human.score - 1000);
      this.sim.quotaMetBy = PLAYER;
      this.sim.winner = PLAYER;
      this.sim.status = 'won';
    } else if (rival) {
      // Rival EDGES it by a small margin → the "so close" loss mood (not a blowout).
      const base = Math.max(human.score, 12900);
      const nearMissCorrect = Math.max(0, this.sim.state.quota - 1);
      const marked = this.markCorrectCellsForCapture(PLAYER, nearMissCorrect);
      human.score = base;
      human.eatenCorrect = Math.max(human.eatenCorrect, marked, nearMissCorrect);
      human.combo = Math.max(human.combo, marked);
      rival.eatenCorrect = Math.max(rival.eatenCorrect, this.sim.state.quota);
      rival.score = base + 190;
      this.sim.quotaMetBy = AI;
      this.sim.winner = AI;
      this.sim.status = 'lost';
    } else {
      // Solo: time + lives out.
      human.lives = 0;
      human.alive = false;
      this.sim.timeLeftMs = 0;
      this.sim.winner = null;
      this.sim.status = 'lost';
    }
  }

  seedCaptureRunLedger(): void {
    const quota = Math.max(1, this.sim?.state?.quota ?? 6);
    const topicLabel = this.topicLabel || this.topic || 'Planets in our solar system';
    replaceRun(this, [
      { boardIndex: 0, topicLabel, humanScore: 12480, aiScore: 9210, winner: PLAYER, won: true, bestCombo: 4, eatenCorrect: quota, quota, threads: 1 },
      { boardIndex: 1, topicLabel, humanScore: 11840, aiScore: 10110, winner: PLAYER, won: true, bestCombo: 3, eatenCorrect: quota, quota, threads: 0 },
      { boardIndex: 2, topicLabel, humanScore: 11260, aiScore: 11940, winner: AI, won: false, bestCombo: 3, eatenCorrect: Math.max(0, quota - 1), quota, threads: 1 },
      { boardIndex: 3, topicLabel, humanScore: 13260, aiScore: 11670, winner: PLAYER, won: true, bestCombo: 5, eatenCorrect: quota, quota, threads: 2 },
    ]);
  }

  // --- Pause controller (extracted; see game/pauseModal.ts) ---
  togglePause = Pause.togglePause;
  openPause = Pause.openPause;
  resumeGame = Pause.resumeGame;

  // --- Round controller (extracted; see game/roundFlow.ts) ---
  openPreRoundCoach = Round.openPreRoundCoach;
  startRound = Round.startRound;
  scheduleEarlyScanHint = Round.scheduleEarlyScanHint;
  showScanTeachBubble = Round.showScanTeachBubble;
  dismissScanTeachBubble = Round.dismissScanTeachBubble;


  // --- per-frame -----------------------------------------------------------
  update(_time: number, delta: number): void {
    if (this.preRoundActive) return;
    if (this.ended || this.paused || this.frameFatal) return; // M3 pause freezes the sim; frameFatal no-ops post-crash.
    // E1: a throw inside ONE frame's tick/render must degrade gracefully — Phaser would otherwise let it
    // propagate out of the rAF loop and freeze the whole game (dead canvas, no recovery). We catch, mark
    // the scene FATAL (so the loop no-ops and can never re-throw — and the pause button can't re-enter the
    // broken tick, which previously caused a silent freeze loop), log ONCE (a per-frame error would spam
    // the console 60×/s), and route to the global "Something glitched — Reload" recovery panel.
    try {
      this.tickFrame(delta);
    } catch (err) {
      this.frameFatal = true;
      this.paused = true;
      if (!this.frameErrorLogged) {
        this.frameErrorLogged = true;
        console.error('[trivia-munchers] GameScene.update frame error (recovering):', err);
      }
      handleFatal('GameScene.tick', err);
    }
  }

  /** The actual per-frame body, extracted so update() can wrap it in a single degrade-not-kill guard. */
  tickFrame(delta: number): void {
    // Pointer run (drag-glide / tap autopath): feed AT MOST ONE queued step per frame. This
    // one-shift-per-frame drain (paired with Core consuming one queued cmd/player/advance) is the
    // TRUE 1-input-buffer safety guarantee — STEP_FEED_MS only paces feel. Drained BEFORE sim.tick
    // so the fed command is enqueued the same frame Core consumes it (input->visual within budget).
    if (this.pendingSteps.length > 0) {
      this.stepFeedAccumMs += delta;
      if (this.stepFeedAccumMs >= STEP_FEED_MS) {
        this.stepFeedAccumMs -= STEP_FEED_MS;
        this.releasePendingStep();
      }
    }
    // #41: poll held movement keys → DAS(140)/ARR(90) auto-repeat, dispatched BEFORE the sim ticks so a
    // repeat is enqueued the same frame Core consumes it (input→visual within budget), just like the
    // STEP_FEED drain above.
    this.pollHeldMovement(delta);
    // Step the sim, then mirror it. Clamp dt so a tab-stall doesn't fast-forward the board.
    const dt = Math.min(delta, 50);
    this.sim.tick(dt);
    const snap = this.sim.snapshot();
    this.renderSnapshot(snap, false);
    this.consumeEvents(snap);
    // GHOST DUELS Slice A: advance the spectral self-race replay to the live sim clock, and retire any
    // challenge-ghost footprints the player has now reached. Both are cheap no-ops when absent/empty.
    this.ghostRacer?.update(snap.timeMs, snap.players.find((p) => p.id === PLAYER)?.score);
    if (this.ghostMarkers.size > 0) pruneGhostMarkers(this, this.ghostMarkers, snap.cells, snap.timeMs);
    // Decay the crescendo music swell toward 0 over ~1200ms (presentation-only; uses the real frame
    // delta, NOT the sim dt, so a hitstop-slowed scene clock doesn't stretch the swell). consumeEvents
    // above may have just SET it to 1, so decay after consuming and before updateMusicIntensity reads it.
    if (this.musicSwell > 0) this.musicSwell = Math.max(0, this.musicSwell - delta / 1200);
    this.updateMusicIntensity(snap);
    // S7: adaptive music director — held-breath on armed traversal + combo/chain tension lift (reads
    // the same snapshot; renderer-only, no Core touch). Runs after intensity so it layers on top.
    this.updateMusicDirector(snap);
    this.maybeIdleNudge(snap);

    if (this.fpsText) {
      this.fpsText.setText(`${Math.round(this.game.loop.actualFps)} fps  seed=${this.seed}`);
    }

    if (snap.status !== 'playing') {
      this.trackBoardCompleted(snap);
      this.ended = true;
      this.cancelPendingSteps(); // board over — drop any remaining glide steps.
      // Hitstop intentionally slows the Phaser scene clock for munch impact. The end-of-board handoff
      // is UX flow, not presentation animation, so keep it on the wall clock; otherwise a board-winning
      // munch can stretch the 420ms clear beat into multi-second dead air in real-time play/video.
      if (this.resultTransitionTimer !== undefined) return;
      // GHOST DUELS Slice A: mint this daily board-0 run as the LOCAL self-race ghost (once). Runs on the
      // wall-clock board-end path, before the handoff; cheap (one log extraction + one localStorage set).
      this.recordDailyGhost(snap);
      // FACE & FURY S5 — the board-end SET-PIECE decides the Result handoff window. PERFECT GAUNTLET
      // already fired its screen-consuming wordmark in the boardWin handler (it OWNS the clear); a close
      // finish (renderer-derived margin) gets the photo-finish bullet-time beat. Both hold longer than
      // the normal 420ms clear so their payoff reads before the scene swaps. All wall-clock (below), so a
      // hitstop can never stretch them (the result-handoff regression guard stays green under budget).
      let handoffMs = 420;
      if (this.perfectGauntlet) {
        handoffMs = Events.PERFECT_GAUNTLET_HANDOFF_MS;
      } else {
        const pf = this.detectPhotoFinish(snap);
        if (pf.close) {
          this.firePhotoFinishBeat(snap.status === 'won', pf.margin);
          handoffMs = Events.PHOTO_FINISH_HANDOFF_MS;
        }
      }
      // Board→Result handoff: a PARTIAL dark veil (alpha → ~0.7 over 140ms, backdrop tone) instead
      // of the old cameras.fadeOut(180) to SOLID — at 25fps capture the full fade read as a hard
      // black frame between scenes (Gemini minor). SKIP the standard veil when a screen-consuming
      // set-piece owns the transition — it draws its own overlay/tag; a second veil would double-dim it.
      if (motionAllowed() && handoffMs === 420) {
        // fillAlpha=1 + a GameObject-alpha ramp: a Shape renders fill at `fillAlpha × alpha`, so a
        // fillAlpha=0 rect never composites no matter the alpha tween (shared S5 round-2 root cause).
        const veil = this.add
          .rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0x080a12, 1)
          .setScrollFactor(0)
          .setAlpha(0)
          .setDepth(12_000);
        this.tweens.add({ targets: veil, alpha: 0.7, duration: 140, ease: 'Quad.easeOut' });
      }
      this.resultTransitionTimer = window.setTimeout(() => {
        this.resultTransitionTimer = undefined;
        if (!this.scene.isActive()) return;
        this.scene.start('Result', {
          snapshot: snap,
          seed: this.seed,
          topic: this.topic,
          topicLabel: this.topicLabel,
          isDaily: this.isDaily,
          dailyMutatorId: this.dailyMutatorId,
          threadsThisBoard: this.threadsThisBoard,
          // FACE & FURY S5: carry the PERFECT GAUNTLET verdict so Result badges the share card + fronts
          // the share prompt. Renderer-derived from the log at board end (detectPerfectGauntlet).
          perfectGauntlet: this.perfectGauntlet,
          peakCombo: this.peakCombo,
          ai: this.aiEnabled,
          rivalWarmup: this.rivalWarmup,
          boardIndex: this.boardIndex,
          boardSource: this.boardSource,
          noviceRun: this.noviceRun,
          challengeTarget: this.challengeTarget,
          // GHOST DUELS Slice C: carry the ladder challenger (non-null only when its racer was actually built)
          // so Result can score the rung + celebrate. null'd in resolvePendingGhost on a pin miss.
          ladderGhost: this.ladderRace,
          // The human's MUNCH-SUM for this run (buildGhostTrack finalScore) — the SAME basis the ghost store
          // derives record.score from. The rung verdict compares munch-sum vs munch-sum so a perfect-board
          // bonus / surge purge (in the full sim score, which the ghost's munch-sum structurally can't have)
          // never fires a phantom "GHOST DOWN". Only computed on a live ladder duel; undefined otherwise.
          humanTrackScore: this.ladderRace ? buildGhostTrack(this.sim.log.all()).finalScore : undefined,
          series: this.series,
        });
      }, handoffMs);
    }
  }

  renderSnapshot(snap: Snapshot, immediate: boolean): void {
    // cells
    for (const cell of snap.cells) {
      const v = this.cellViews[cell.id];
      if (!v) continue;
      this.renderCell(cell, v, immediate);
    }
    // munchers
    const human = snap.players.find((p) => p.id === PLAYER);
    if (human) {
      // Life-lost SFX/haptic edge-detect (hazardHit event handles the screen flash; this catches the
      // actual life decrement from any cause and gives it a distinct, heavier cue). A11y: pair the
      // sound with an explicit VISUAL — a "-1 ♥" float by the lives counter — so a player with sound
      // off (or who is deaf/HoH) still sees the loss even when it isn't a hazard flash.
      const lostLife = this.prevLives >= 0 && human.lives < this.prevLives && !immediate;
      if (lostLife) {
        this.cancelPendingSteps(); // a stun/teleport aborts the route rather than sleep-walking post-respawn.
        audio().playSfx('lifeLost');
        haptic([60, 40, 60]);
        if (this.heartRowC) {
          floatText(this, this.heartRowC.x + 16, this.heartRowC.y + 16, '-1 ♥', hex(PALETTE.hazard), 16);
        }
      }
      // VG-10: a life loss makes Core SNAP the human back to its start cell. Capture where the sprite WAS
      // (its pre-reset SCREEN position) and whether the authoritative pos actually jumped, so the
      // moveSprite() snap below can be dressed as a deliberate death→respawn beat instead of a teleport.
      const deathFromX = this.humanSprite.x;
      const deathFromY = this.humanSprite.y;
      const posJumped = lostLife && this.prevHumanPos >= 0 && human.pos !== this.prevHumanPos && human.alive;
      this.prevLives = human.lives;
      this.prevHumanPos = human.pos;
      // Eat-moves INTERPOLATE too (a quick ~130ms slide into the eaten cell + munch burst reads far
      // smoother than a teleport, and the eat VFX fires at the destination cell so it stays aligned).
      // Only true respawns/teleports (`immediate`) snap. Starts on the same frame, so the input→visual
      // ≤2-frame budget holds; the sim position is unchanged (cosmetic render-interpolation only).
      this.moveSprite(this.humanSprite, human.pos, immediate, human.stunnedUntilMs > snap.timeMs);
      // Fire the cue ONCE per repositioning death, AFTER moveSprite has snapped the real sprite to the
      // authoritative new cell (so the materialize plays on the sprite already at the correct position —
      // never desyncing it). A non-repositioning life loss (e.g. a board-lose with pos unchanged) skips it.
      if (posJumped) this.playRespawnCue(deathFromX, deathFromY);
      this.updatePlayerAuras(snap, human);
    }
    if (this.aiEnabled) {
      const ai = snap.players.find((p) => p.id === AI);
      if (ai) {
        this.aiSprite.setVisible(ai.alive);
        this.moveSprite(this.aiSprite, ai.pos, immediate, false);
      }
    }
    this.updateAiPressureCue(snap);
    // hazards
    const live = new Set<number>();
    for (const hz of snap.hazards) {
      live.add(hz.id);
      let s = this.hazardSprites.get(hz.id);
      if (!s) {
        s = this.makeHazardSprite(hz.type);
        // VG-7: persistent red threat aura behind the hazard so it reads as a danger, not the AI rival.
        this.addHazardDangerAura(s, this.munchRadius() * 0.9, hz.id);
        this.hazardSprites.set(hz.id, s);
        const c = this.cellCenter(hz.pos);
        s.setPosition(c.x, c.y);
      }
      this.moveSprite(s, hz.pos, immediate, false);
    }
    for (const [id, s] of this.hazardSprites) {
      if (!live.has(id)) {
        this.removeHazardDangerAura(id); // kill the threat-aura pulse before the sprite is destroyed.
        s.destroy();
        this.hazardSprites.delete(id);
      }
    }

    this.applyLabelOcclusion(snap);
    this.updateHud(snap);
    // STRETCH Lane A: ease the RIVAL PRESSURE standing, decay the menace focal window, apply the
    // one-focal-point aiPressureCue suppression, and detect a lead EXTEND. Runs AFTER updateHud (whose
    // updateRivalReadout may fire the flip menace beat) + AFTER updateAiPressureCue, so the focal dim lands
    // the same frame. Cheap: dirty-checked repaint. No-op when the broadcast package isn't built.
    this.broadcast?.update(snap);
  }

  /**
   * Throttled, low-volume movement tick so stepping has a tactile sound (the one verb that was silent)
   * without fatigue: a min-gap throttle (~step cadence) + a hair of pitch variation so a run doesn't
   * machine-gun the same blip. Only the human's steps tick.
   */
  lastMoveStepMs = -1;

  // --- Adaptive music director (S7; see game/musicDirector.ts) ---
  updateMusicDirector = Director.updateMusicDirector;

  // --- Events controller (extracted; see game/eventFeedback.ts) ---
  updateMusicIntensity = Events.updateMusicIntensity;
  consumeEvents = Events.consumeEvents;
  fireGauntletChainCrescendo = Events.fireGauntletChainCrescendo;
  playOptionalCrescendoBark = Events.playOptionalCrescendoBark;
  showGauntletChainStamp = Events.showGauntletChainStamp;
  showFlawlessFeast = Events.showFlawlessFeast;
  showFirstBiteStamp = Events.showFirstBiteStamp;
  /**
   * S9 FIX-C caption-band arbiter wrapper around Events.showTeachBeat (G0 AMATEURs 4-7 / G1 item 5).
   * LANDSCAPE: the card's bottom fallback slot resolves INSIDE the near-full-height board (it clipped
   * the bottom tile row + stacked on the SKIPPED pill), so the teach line routes through the ONE
   * rail-docked caption slot instead (showCommandHint — newest coaching wins the lane; the SKIPPED
   * pill already serialises against that slot). PORTRAIT keeps the above-grid card but yields the
   * 'YOU ARE ON' status line for the card's life (fade-in 160 + hold 1350 + fade 280 ≈ 1.8s) so the
   * plate can never clip it. Wiring only — the card itself is untouched in eventFeedback.ts.
   */
  showTeachBeat = (message: string): void => {
    if (isLandscape()) {
      this.showCommandHint(message, PALETTE.armed, 4200);
      return;
    }
    this.underTextYieldUntilMs = this.time.now + 1900;
    Events.showTeachBeat.call(this, message);
  };
  // FACE & FURY S5 — board-end signature set-pieces (see game/eventFeedback.ts).
  clearChainSpectacle = Events.clearChainSpectacle;
  detectPerfectGauntlet = Events.detectPerfectGauntlet;
  firePerfectGauntletSetPiece = Events.firePerfectGauntletSetPiece;
  showPerfectGauntletWordmark = Events.showPerfectGauntletWordmark;
  detectPhotoFinish = Events.detectPhotoFinish;
  firePhotoFinishBeat = Events.firePhotoFinishBeat;
  showPhotoFinishTag = Events.showPhotoFinishTag;
  showPhotoFinishBleach = Events.showPhotoFinishBleach;
  showPhotoFinishVignette = Events.showPhotoFinishVignette;
  showPhotoFinishStreaks = Events.showPhotoFinishStreaks;
  playOptionalSetPieceBark = Events.playOptionalSetPieceBark;

}

function boardSourceFromContent(content: BoardContent): BoardSource {
  if (content.source === 'generated') return 'generated';
  if (content.source === 'seed') return 'seed';
  return 'unknown';
}
